import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import L from "leaflet";
import { addGrayscaleBasemapControl } from "../lib/mapBasemap";
import "leaflet/dist/leaflet.css";
import { apiJson } from "../lib/queryClient";
import type { Drone, FlightSession, WizardData } from "../lib/planning/types";
import {
  COUNTRY_MAPS,
  buildMapUrl,
  detectCountryFromCoordinates,
  detectBundeslandFromCoordinates,
  getCountryMapConfig,
  getMapConfigOrFallback,
  hasOwnMap,
} from "../lib/planning/countryMaps";
import {
  PARAGRAPH_21H_RULES,
  BUNDESLAND_AV_INFO,
  migrateAllgemeinerlaubnis,
  isErlaubnisExpired,
  assessAvCoverage,
  type GeoAllgemeinerlaubnis,
} from "../lib/allgemeinerlaubnis";
import { PLANNING_CHECKLIST, FPV_PLANNING_ITEMS } from "../lib/planning/checklists";
import { deriveSuggestedRestrictionStatus } from "../lib/planning/restrictionDerivation";
import { fetchForecast, describeWind, type ForecastResult } from "../lib/planning/weather";
import {
  fetchNotams,
  geocodeSearch,
  reverseGeocode,
  type GeocodeResult,
  type NotamRow,
} from "../lib/external";
import {
  STATUS_COLORS,
  STATUS_LABELS,
  assessPlanning,
} from "../lib/planning/assessment";
import PlanningGeozoneMap from "../components/PlanningGeozoneMap";
import GermanEnergyInfrastructurePanel from "../components/GermanEnergyInfrastructurePanel";
import { useGermanEnergyInfrastructure } from "../lib/useGermanEnergyInfrastructure";
import { useSubscription } from "../lib/subscription";
import { isPlanningCountryLocked } from "../lib/planning/mapStepGate";
import { countryName } from "../../../shared/countryEntitlements";
import { PawPrint } from "lucide-react";
import { WildlifeRescueInfoModal } from "../components/WildlifeRescueInfo";
import {
  WILDLIFE_RESCUE_MODE_TITLE,
  WILDLIFE_RESCUE_PLANNING_HINT,
} from "@shared/wildlifeRescue";
import { hasIncompleteEnergyInfrastructureSources } from "@shared/germanEnergyInfrastructure";

const DEFAULT_LIMITS = {
  maxWind: 10,
  maxGusts: 12,
  maxRain: 0.5,
  minTemp: 0,
  maxTemp: 40,
  minCloudCeiling: 50,
  minVisibility: 5000,
};

const STEPS = [
  "Drohne",
  "Land",
  "Standort",
  "Karte",
  "Gegenprüfung",
  "Datum/Zeit",
  "Restriktionen",
  "NOTAMs",
  "Wetter",
  "FPV",
  "Checkliste",
  "Ergebnis",
] as const;

// Index des eigenen Gegenprüfungs-Schritts (offizielle Staatskarte). Wird nur
// für Länder MIT eigenen Geozonen-Daten und aktivierter Einstellung gezeigt;
// sonst in next()/back() übersprungen.
const CROSSCHECK_STEP = 4;

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function nowTimeStr() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const EMPTY: WizardData = {
  droneId: null,
  countryIso: "DE",
  latitude: null,
  longitude: null,
  locationName: "",
  plannedDate: todayStr(),
  plannedTime: nowTimeStr(),
  durationMinutes: 30,
  maxAltitude: 100,
  hasRestrictions: false,
  restrictionStatus: "none",
  restrictionNotes: "",
  detectedEnergyInfrastructureCount: 0,
  isFpvFlight: false,
  flightPurpose: "",
  checklistCompleted: [],
};

export default function PlanningWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isResume = id && id !== "neu";

  const [step, setStep] = useState(0);
  const [data, setData] = useState<WizardData>(EMPTY);
  const [sessionId, setSessionId] = useState<string | null>(isResume ? id! : null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: drones = [] } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });
  // Einstellung "Offizielle Karte gegenprüfen" (Default an). Steuert, ob für
  // Länder MIT eigenen Daten der eigene Gegenprüfungs-Schritt erscheint.
  const { data: settings } = useQuery<{ officialMapCrossCheck?: boolean } | null>({
    queryKey: ["/api/settings"],
  });
  const crossCheckEnabled = settings?.officialMapCrossCheck ?? true;
  // Länder-Berechtigung (Task #561): Die offizielle Länderkarte ist ein
  // Pflichtschritt. Ist das geplante Land nicht freigeschaltet, wird der Schritt
  // gesperrt und die Planung kann nicht fortgesetzt werden.
  const { data: subscription } = useSubscription();
  const countryLocked = isPlanningCountryLocked(
    subscription?.entitlement,
    data.countryIso,
  );
  const drone = drones.find((d) => d.id === data.droneId) || null;
  const limits = drone?.limits || DEFAULT_LIMITS;
  // Eigener Gegenprüfungs-Schritt nur für Länder MIT eigenen Geozonen-Daten und
  // aktivierter Einstellung. Sonst wird Schritt CROSSCHECK_STEP übersprungen.
  const showCrossCheckStep = hasOwnMap(data.countryIso) && crossCheckEnabled;

  // Resume: load session
  const { data: existing } = useQuery<FlightSession>({
    queryKey: ["/api/sessions", id ?? ""],
    enabled: !!isResume,
  });

  useEffect(() => {
    if (!isResume || !existing) return;
    const p: Partial<WizardData> = existing.planning || {};
    setData({
      ...EMPTY,
      droneId: existing.droneId,
      countryIso: existing.countryIso || "DE",
      latitude: existing.latitude,
      longitude: existing.longitude,
      locationName: existing.locationName || "",
      hasRestrictions: existing.hasRestrictions || false,
      restrictionNotes: existing.restrictionNotes || "",
      detectedEnergyInfrastructureCount:
        typeof p.detectedEnergyInfrastructureCount === "number"
          ? p.detectedEnergyInfrastructureCount
          : 0,
      restrictionStatus: existing.hasRestrictions
        ? (existing.restrictionNotes ? "limited" : "none")
        : "none",
      plannedDate: p.plannedDate || todayStr(),
      plannedTime: p.plannedTime || nowTimeStr(),
      durationMinutes: p.durationMinutes || 30,
      maxAltitude: p.maxAltitude || 100,
      manualRestrictionChecked: p.manualRestrictionChecked || false,
      isFpvFlight: p.isFpvFlight || false,
      flightPurpose: p.flightPurpose || "",
      checklistCompleted: p.checklistCompleted || [],
      forecastWeather: p.forecastWeather,
      notamCount: p.notamCount,
      notamCheckedAt: p.notamCheckedAt,
      officialMapCrossCheck: p.officialMapCrossCheck,
    });
  }, [existing, isResume]);

  const update = (patch: Partial<WizardData>) =>
    setData((prev) => {
      const locationChanged =
        ("latitude" in patch && patch.latitude !== prev.latitude) ||
        ("longitude" in patch && patch.longitude !== prev.longitude) ||
        ("countryIso" in patch && patch.countryIso !== prev.countryIso);
      return {
        ...prev,
        ...(locationChanged ? { detectedEnergyInfrastructureCount: 0 } : {}),
        ...patch,
      };
    });

  // Persist session (create or update)
  async function persist(extra?: Partial<FlightSession>) {
    if (!data.droneId || data.latitude == null || data.longitude == null) return;
    setSaving(true);
    try {
      const mapConfig = getCountryMapConfig(data.countryIso);
      const mapUrl = mapConfig
        ? buildMapUrl(mapConfig, data.latitude, data.longitude)
        : null;
      const payload = {
        droneId: data.droneId,
        currentPhase: "planning",
        latitude: data.latitude,
        longitude: data.longitude,
        locationName: data.locationName || null,
        countryIso: data.countryIso,
        mapUrl,
        hasRestrictions:
          data.hasRestrictions ||
          (data.countryIso === "DE" &&
            (data.detectedEnergyInfrastructureCount ?? 0) > 0),
        restrictionNotes: data.restrictionNotes || null,
        status: extra?.status || "caution",
        // reasons NUR senden, wenn ein Array vorliegt. Die DB-Spalte ist nullable,
        // aber das Insert-Schema akzeptiert nur array|undefined (kein null) — ein
        // hier sonst gesendetes `null` ließe den PUT/POST mit
        // "reasons: Expected array, received null" als 400 scheitern.
        reasons: extra?.reasons ?? undefined,
        planning: {
          plannedDate: data.plannedDate,
          plannedTime: data.plannedTime,
          maxAltitude: data.maxAltitude,
          durationMinutes: data.durationMinutes,
          isFpvFlight: data.isFpvFlight,
          flightPurpose: data.flightPurpose || undefined,
          checklistCompleted: data.checklistCompleted,
          forecastWeather: data.forecastWeather,
          notamCount: data.notamCount,
          notamCheckedAt: data.notamCheckedAt,
          manualRestrictionChecked: data.manualRestrictionChecked,
          officialMapCrossCheck: data.officialMapCrossCheck,
          detectedEnergyInfrastructureCount:
            data.countryIso === "DE"
              ? (data.detectedEnergyInfrastructureCount ?? 0)
              : 0,
        },
        ...extra,
      };
      let saved: FlightSession;
      if (sessionId) {
        saved = await apiJson<FlightSession>("PUT", `/api/sessions/${sessionId}`, payload);
      } else {
        saved = await apiJson<FlightSession>("POST", "/api/sessions", payload);
        setSessionId(saved.id);
      }
      qc.invalidateQueries({ queryKey: ["/api/sessions"] });
      return saved;
    } finally {
      setSaving(false);
    }
  }

  function validateCurrent(): string | null {
    switch (step) {
      case 0:
        return data.droneId ? null : "Bitte eine Drohne auswählen.";
      case 1:
        return data.countryIso ? null : "Bitte ein Land auswählen.";
      case 2:
        if (data.latitude == null || data.longitude == null)
          return "Bitte Koordinaten angeben.";
        if (Math.abs(data.latitude) > 90 || Math.abs(data.longitude) > 180)
          return "Koordinaten ungültig.";
        return null;
      case 3:
        if (countryLocked)
          return `${countryName(data.countryIso)} ist nicht freigeschaltet. Bitte das Land freischalten, um die Planung fortzusetzen.`;
        return null;
      case 5:
        if (!data.plannedDate || !data.plannedTime)
          return "Bitte Datum und Uhrzeit angeben.";
        return null;
      default:
        return null;
    }
  }

  async function next() {
    setError(null);
    const err = validateCurrent();
    if (err) {
      setError(err);
      return;
    }
    // Persist as soon as we have enough data (after location step)
    if (step >= 2 && data.droneId && data.latitude != null && data.longitude != null) {
      try {
        await persist();
      } catch (e) {
        setError((e as Error).message || "Speichern fehlgeschlagen");
        return;
      }
    }
    setStep((s) => {
      let n = Math.min(s + 1, STEPS.length - 1);
      // Gegenprüfungs-Schritt überspringen, wenn nicht anwendbar.
      if (n === CROSSCHECK_STEP && !showCrossCheckStep) n += 1;
      return Math.min(n, STEPS.length - 1);
    });
  }

  function back() {
    setError(null);
    setStep((s) => {
      let n = Math.max(0, s - 1);
      // Gegenprüfungs-Schritt überspringen, wenn nicht anwendbar.
      if (n === CROSSCHECK_STEP && !showCrossCheckStep) n -= 1;
      return Math.max(0, n);
    });
  }

  async function saveAndExit() {
    setError(null);
    if (step >= 2 && data.droneId && data.latitude != null && data.longitude != null) {
      try {
        await persist();
      } catch (e) {
        setError((e as Error).message || "Speichern fehlgeschlagen");
        return;
      }
    }
    navigate("/flugplanung");
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-base font-semibold text-[var(--color-app-text)]">
            Schritt {step + 1} von {STEPS.length}
          </p>
          <p className="text-[var(--color-app-text-secondary)] text-sm mt-1">{STEPS[step]}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/flugplanung")}
            className="btn-secondary text-sm"
          >
            Abbrechen
          </button>
          {step >= 2 && (
            <button
              type="button"
              onClick={saveAndExit}
              className="btn-secondary text-sm"
              disabled={saving}
            >
              {saving ? "Speichert …" : "Speichern & beenden"}
            </button>
          )}
        </div>
      </div>

      <Stepper step={step} />

      <div className="card space-y-5">
        {step === 0 && <StepDrone data={data} update={update} drones={drones} />}
        {step === 1 && <StepCountry data={data} update={update} />}
        {step === 2 && <StepLocation data={data} update={update} />}
        {step === 3 && (
          <StepMap data={data} update={update} countryLocked={countryLocked} />
        )}
        {step === 4 && <StepCrossCheck data={data} update={update} />}
        {step === 5 && <StepDateTime data={data} update={update} />}
        {step === 6 && <StepRestrictions data={data} update={update} />}
        {step === 7 && <StepNotams data={data} update={update} />}
        {step === 8 && <StepWeather data={data} update={update} />}
        {step === 9 && <StepFpv data={data} update={update} />}
        {step === 10 && (
          <StepChecklist data={data} update={update} drone={drone || undefined} />
        )}
        {step === 11 && (
          <StepResult
            data={data}
            drone={drone || undefined}
            limits={limits}
            sessionId={sessionId}
            persist={persist}
            navigate={navigate}
            qc={qc}
          />
        )}

        {error && (
          <div
            data-testid="planning-step-error"
            className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded-md px-3 py-2"
          >
            {error}
          </div>
        )}
      </div>

      {step < 11 && (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="btn-secondary disabled:opacity-50"
          >
            ← Zurück
          </button>
          <button type="button" onClick={next} className="btn-primary" disabled={saving}>
            {saving ? "Speichert …" : "Weiter →"}
          </button>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }: { step: number }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-1 shrink-0">
          <div
            className={`size-7 rounded-full grid place-items-center text-xs font-semibold ${
              i < step
                ? "bg-[var(--color-go)] text-white"
                : i === step
                ? "bg-brand-500 text-white"
                : "bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)]"
            }`}
          >
            {i + 1}
          </div>
          <div
            className={`text-xs whitespace-nowrap ${
              i === step ? "text-[var(--color-app-text)] font-medium" : "text-[var(--color-app-text-tertiary)]"
            }`}
          >
            {label}
          </div>
          {i < STEPS.length - 1 && (
            <div className="w-4 h-px bg-[var(--color-app-bg-tertiary)] mx-1" />
          )}
        </div>
      ))}
    </div>
  );
}

/* --------------------------- Step components --------------------------- */

function StepDrone({
  data,
  update,
  drones,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
  drones: Drone[];
}) {
  if (drones.length === 0) {
    return (
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Du hast noch keine Drohnen angelegt. Bitte zuerst unter „Drohnen“ eine
        anlegen.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--color-app-text-secondary)]">Wähle die Drohne für diesen Flug.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {drones.map((d) => {
          const selected = d.id === data.droneId;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => update({ droneId: d.id })}
              className={`text-left rounded-lg border-2 px-4 py-3 transition ${
                selected
                  ? "border-brand-500 bg-brand-50"
                  : "border-[var(--color-app-border)] hover:border-brand-300"
              }`}
            >
              <div className="font-semibold text-[var(--color-app-text)]">{d.name}</div>
              <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">
                {[d.manufacturer, d.model].filter(Boolean).join(" ") || "—"}
              </div>
              <div className="text-xs text-[var(--color-app-text-secondary)] mt-2">
                {d.classMark} · {d.openCategory} · {d.mtomGrams} g
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepCountry({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Wähle das Land, in dem geflogen wird. Daraus ergibt sich später die
        Geozonen-Karte.
      </p>
      <div>
        <label className="label">Land</label>
        <select
          className="input"
          value={data.countryIso}
          onChange={(e) => update({ countryIso: e.target.value })}
        >
          {COUNTRY_MAPS.map((c) => (
            <option key={c.iso} value={c.iso}>
              {c.nameDE} ({c.iso})
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

const LOCATION_MARKER_ICON = L.icon({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function StepLocation({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  const [geoErr, setGeoErr] = useState<string | null>(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  function applyLocation(lat: number, lon: number, label?: string) {
    const country = detectCountryFromCoordinates(lat, lon);
    update({
      latitude: lat,
      longitude: lon,
      countryIso: country || data.countryIso,
      ...(label ? { locationName: label } : {}),
    });
  }

  // Initialise the map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initialLat = data.latitude ?? 51.1657;
    const initialLon = data.longitude ?? 10.4515;
    const initialZoom = data.latitude != null && data.longitude != null ? 13 : 6;
    const map = L.map(containerRef.current, {
      center: [initialLat, initialLon],
      zoom: initialZoom,
      zoomControl: true,
    });
    const baseLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    addGrayscaleBasemapControl(map, baseLayer);
    map.on("click", (e: L.LeafletMouseEvent) => {
      applyLocation(e.latlng.lat, e.latlng.lng);
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the marker with the selected coordinates
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (data.latitude == null || data.longitude == null) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    const ll: L.LatLngExpression = [data.latitude, data.longitude];
    if (!markerRef.current) {
      markerRef.current = L.marker(ll, { icon: LOCATION_MARKER_ICON, draggable: true }).addTo(map);
      markerRef.current.on("dragend", (e) => {
        const m = e.target as L.Marker;
        const p = m.getLatLng();
        applyLocation(p.lat, p.lng);
      });
    } else {
      markerRef.current.setLatLng(ll);
    }
    map.setView(ll, Math.max(map.getZoom(), 13), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.latitude, data.longitude]);

  function detect() {
    if (!navigator.geolocation) {
      setGeoErr("Browser unterstützt keine Standortbestimmung.");
      return;
    }
    setGeoBusy(true);
    setGeoErr(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        applyLocation(lat, lon);
        setGeoBusy(false);
        try {
          const r = await reverseGeocode(lat, lon);
          if (r.display_name && !data.locationName) {
            update({ locationName: r.display_name });
          }
        } catch {}
      },
      (err) => {
        setGeoErr(err.message || "Standort konnte nicht ermittelt werden.");
        setGeoBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  // Debounced geocoding search as the user types
  useEffect(() => {
    const q = search.trim();
    if (q.length < 3) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await geocodeSearch(q);
        setResults(r.slice(0, 8));
        setSearchOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [search]);

  function pickResult(r: GeocodeResult) {
    const lat = parseFloat(r.lat);
    const lon = parseFloat(r.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;
    applyLocation(lat, lon, r.display_name);
    setSearch(r.display_name);
    setSearchOpen(false);
    setResults([]);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Suche nach einem Ort, tippe auf die Karte oder verwende deinen aktuellen
        Standort. Den Marker kannst du per Drag & Drop verschieben.
      </p>

      <div>
        <label className="label">Ortsbezeichnung</label>
        <input
          className="input"
          placeholder="z.B. Wiese am Sportplatz"
          value={data.locationName}
          onChange={(e) => update({ locationName: e.target.value })}
        />
      </div>

      <div className="relative">
        <label className="label">Ort suchen</label>
        <input
          className="input"
          placeholder="Stadt, Adresse oder POI eingeben…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => results.length > 0 && setSearchOpen(true)}
        />
        {searching && (
          <div className="absolute right-3 top-9 text-xs text-[var(--color-app-text-tertiary)]">Suche …</div>
        )}
        {searchOpen && results.length > 0 && (
          <ul className="absolute z-[1000] mt-1 max-h-64 w-full overflow-auto rounded-md border border-[var(--color-app-border)] bg-white shadow-lg">
            {results.map((r, i) => (
              <li key={`${r.lat}-${r.lon}-${i}`}>
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-app-bg-tertiary)]"
                  onClick={() => pickResult(r)}
                >
                  {r.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        ref={containerRef}
        className="h-72 w-full overflow-hidden rounded-lg border border-[var(--color-app-border)]"
        style={{ minHeight: 280 }}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={detect}
          className="btn-secondary text-sm"
          disabled={geoBusy}
        >
          {geoBusy ? "Ermittelt …" : "Aktuellen Standort verwenden"}
        </button>
        {data.latitude != null && data.longitude != null ? (
          <div className="text-xs text-[var(--color-app-text-secondary)]">
            {data.latitude.toFixed(5)}, {data.longitude.toFixed(5)} ·{" "}
            {detectCountryFromCoordinates(data.latitude, data.longitude) || "—"}
          </div>
        ) : (
          <div className="text-xs text-[var(--color-app-text-tertiary)]">Noch kein Standort gewählt</div>
        )}
      </div>
      {geoErr && <span className="text-xs text-[var(--color-nogo)]">{geoErr}</span>}

      <div>
        <label className="label">Max. Flughöhe (m AGL)</label>
        <input
          className="input"
          type="number"
          min={0}
          value={data.maxAltitude}
          onChange={(e) =>
            update({ maxAltitude: parseInt(e.target.value, 10) || 0 })
          }
          data-testid="input-max-altitude"
        />
        {data.maxAltitude > 120 && (
          <div className="text-xs text-[var(--color-caution)] mt-1">
            Über 120 m: Sondergenehmigung erforderlich.
          </div>
        )}
        <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
          Die Flughöhe bestimmt, welche Geozonen auf der folgenden Karte relevant
          sind.
        </p>
      </div>
    </div>
  );
}

function StepMap({
  data,
  update,
  countryLocked,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
  countryLocked: boolean;
}) {
  if (data.latitude == null || data.longitude == null) {
    return <p className="text-sm text-[var(--color-app-text-secondary)]">Bitte zuerst Standort eingeben.</p>;
  }

  // Länder-Berechtigung (Task #561): Ist das geplante Land nicht freigeschaltet,
  // bleibt der Karten-Schritt sichtbar (Geozonen werden serverseitig leer
  // ausgeliefert bzw. grau dargestellt), aber die Planung lässt sich für dieses
  // Land nicht fortsetzen (siehe validateCurrent). Ein Hinweis verlinkt auf den
  // Kauf des Landes.
  const lockedBanner = countryLocked ? (
    <div className="space-y-3" data-testid="planning-country-locked">
      <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-4">
        <p className="text-sm font-semibold text-[var(--color-caution)]">
          {countryName(data.countryIso)} ist nicht freigeschaltet
        </p>
        <p className="mt-1 text-sm text-[var(--color-caution)]">
          Die offizielle Länderkarte und die Geozonen-Prüfung sind nur für
          freigeschaltete Länder verfügbar. Schalte {countryName(data.countryIso)}{" "}
          frei, um die Flugplanung für dieses Land fortzusetzen.
        </p>
      </div>
      <Link
        to="/pakete"
        className="btn-primary inline-block text-sm"
        data-testid="link-unlock-country"
      >
        Land freischalten →
      </Link>
    </div>
  ) : null;

  const ownData = hasOwnMap(data.countryIso);

  // Länder mit eigenen Daten: UNSERE eigene Karte mit Geozonen-Overlay
  // (gleiche Daten/Darstellung wie die große Karte), zentriert auf den Standort.
  if (ownData) {
    const config = getCountryMapConfig(data.countryIso);
    const nameDE = config?.nameDE ?? data.countryIso;
    return (
      <div className="space-y-3">
        {lockedBanner}
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Eigene Geozonen-Karte für {nameDE}. Prüfe Restriktionen, CTRs und
          Verbotszonen rund um deinen Standort.
        </p>
        <PlanningGeozoneMap
          latitude={data.latitude}
          longitude={data.longitude}
          countryIso={data.countryIso}
          reachableAltitudeM={data.maxAltitude}
        />
      </div>
    );
  }

  // Länder ohne eigene Daten: offizielle Staatskarte bzw. EASA-Global-Fallback.
  // Wir können keine Geozonen anzeigen — der Pilot prüft die offizielle Karte
  // selbst und gibt danach an, ob am Standort Einschränkungen bestehen. Die
  // Antwort wird durch die Planungs-Kette gereicht (siehe StepRestrictions).
  return (
    <div className="space-y-3">
      {lockedBanner}
      <StepMapManualRestriction data={data} update={update} />
    </div>
  );
}

function StepMapManualRestriction({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  const lat = data.latitude as number;
  const lon = data.longitude as number;
  const config = getMapConfigOrFallback(data.countryIso);
  const isEasa = config.iso === "EASA";
  const url = buildMapUrl(config, lat, lon);

  const [noteDraft, setNoteDraft] = useState(data.restrictionNotes || "");
  const recorded = !!data.manualRestrictionChecked;

  function answer(hasRestrictions: boolean) {
    update({
      manualRestrictionChecked: true,
      hasRestrictions,
      restrictionStatus: hasRestrictions ? "limited" : "none",
      restrictionNotes: noteDraft.trim(),
    });
  }

  function resetAnswer() {
    update({ manualRestrictionChecked: false });
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-3">
        <p className="text-sm font-semibold text-[var(--color-caution)]">
          Geozonen können für {isEasa ? "dieses Land" : config.nameDE} nicht angezeigt werden
        </p>
        <p className="mt-1 text-sm text-[var(--color-caution)]">
          {isEasa
            ? "Für dieses Land haben wir keine eigenen Geozonen-Daten. Bitte prüfe die offizielle Karte selbst — nutze das EASA-Drohnenportal, um die zuständige Luftfahrtbehörde und deren Karte zu finden."
            : `Für ${config.nameDE} haben wir keine eigenen Geozonen-Daten. Bitte prüfe die offizielle Staatskarte selbst${
                config.supportsCoordinates ? " (zentriert auf deinen Standort)" : ""
              }.`}
        </p>
      </div>

      {config.iframeable ? (
        <div className="rounded-lg overflow-hidden border border-[var(--color-app-border)] bg-[var(--color-app-bg-tertiary)]">
          <iframe
            src={url}
            title={`Offizielle Karte ${config.nameDE}`}
            className="w-full h-[60vh] min-h-[420px]"
          />
        </div>
      ) : (
        <div className="card bg-[var(--color-app-bg-secondary)]">
          <p className="text-sm text-[var(--color-app-text)]">
            {isEasa
              ? "Das EASA-Portal kann nicht eingebettet werden."
              : `Die offizielle Karte für ${config.nameDE} kann nicht eingebettet werden.`}{" "}
            Bitte in einem neuen Tab öffnen:
          </p>
        </div>
      )}

      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="btn-secondary inline-block text-sm"
      >
        {isEasa ? "EASA-Portal öffnen" : "Offizielle Karte ansehen"} →
      </a>

      <div className="rounded-md border border-[var(--color-app-border)] bg-[var(--color-app-bg-secondary)] p-3 space-y-3">
        <p className="text-sm font-semibold text-[var(--color-app-text)]">
          Bestehen laut offizieller Karte Einschränkungen an deinem geplanten Standort?
        </p>

        {recorded ? (
          <div
            className={`rounded-md border p-3 ${
              data.hasRestrictions
                ? "border-[var(--color-caution)] bg-[var(--color-caution-bg)]"
                : "border-[var(--color-go)] bg-[var(--color-go-bg)]"
            }`}
            data-testid="manual-restriction-summary"
          >
            <p
              className={`text-sm font-semibold ${
                data.hasRestrictions ? "text-[var(--color-caution)]" : "text-[var(--color-go)]"
              }`}
            >
              {data.hasRestrictions
                ? "Einschränkungen am Standort"
                : "Keine Einschränkungen am Standort"}
            </p>
            {data.restrictionNotes ? (
              <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">Notiz: {data.restrictionNotes}</p>
            ) : null}
            <button
              type="button"
              onClick={resetAnswer}
              className="mt-2 text-xs font-semibold text-brand-600 hover:underline"
              data-testid="button-edit-restriction-answer"
            >
              Angabe ändern
            </button>
          </div>
        ) : (
          <>
            <div>
              <label className="label">Notiz (optional)</label>
              <textarea
                className="input min-h-[70px]"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="z.B. Naturschutzgebiet am Westrand, NOTAM aktiv …"
                data-testid="input-restriction-note"
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => answer(false)}
                className="flex-1 rounded-md border-2 border-[var(--color-go)] bg-[var(--color-go-bg)] px-3 py-2 text-sm font-semibold text-[var(--color-go)] hover:bg-[var(--color-go-bg)]"
                data-testid="button-restriction-no"
              >
                Nein, keine Einschränkungen
              </button>
              <button
                type="button"
                onClick={() => answer(true)}
                className="flex-1 rounded-md border-2 border-[var(--color-caution)] bg-[var(--color-caution-bg)] px-3 py-2 text-sm font-semibold text-[var(--color-caution)] hover:bg-[var(--color-caution-bg)]"
                data-testid="button-restriction-yes"
              >
                Ja, Einschränkungen bestehen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Eigener Planungs-Schritt: Gegenprüfung der offiziellen Staatskarte für Länder
// MIT eigenen Daten. Erscheint NACH der Standort-/Kartenwahl und ersetzt die
// automatische Geozonen-/§21h-Erkennung NICHT. Das Ergebnis wird separat in
// officialMapCrossCheck geführt (nicht in restrictionStatus).
function StepCrossCheck({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  if (data.latitude == null || data.longitude == null) {
    return (
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Bitte zuerst Standort eingeben.
      </p>
    );
  }

  const lat = data.latitude as number;
  const lon = data.longitude as number;
  const config = getMapConfigOrFallback(data.countryIso);
  const url = buildMapUrl(config, lat, lon);

  const recorded = data.officialMapCrossCheck != null;
  const blocking = data.officialMapCrossCheck?.blocking === true;
  const [noteDraft, setNoteDraft] = useState(data.officialMapCrossCheck?.note || "");

  function answer(isBlocking: boolean) {
    const note = noteDraft.trim();
    update({
      officialMapCrossCheck: { blocking: isBlocking, note: note || undefined },
    });
  }

  function resetAnswer() {
    update({ officialMapCrossCheck: undefined });
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-[var(--color-app-text)]">
        Gegenprüfung mit der offiziellen Staatskarte
      </p>
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Vergleiche unsere Karte zur Sicherheit mit der offiziellen Karte von{" "}
        {config.nameDE}. Diese Gegenprüfung ersetzt die automatische Erkennung nicht.
      </p>

      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="btn-secondary inline-block text-sm"
        data-testid="link-crosscheck-map"
      >
        Offizielle Karte öffnen →
      </a>

      <p className="text-sm font-semibold text-[var(--color-app-text)]">
        Gibt es dort Geozonen, die einen Start verhindern würden?
      </p>

      {recorded ? (
        <div
          className={`rounded-md border p-3 ${
            blocking ? "border-[var(--color-caution)] bg-[var(--color-caution-bg)]" : "border-[var(--color-go)] bg-[var(--color-go-bg)]"
          }`}
          data-testid="crosscheck-summary"
        >
          <p
            className={`text-sm font-semibold ${
              blocking ? "text-[var(--color-caution)]" : "text-[var(--color-go)]"
            }`}
          >
            {blocking
              ? "Startverhindernde Geozonen gemeldet"
              : "Keine startverhindernden Geozonen"}
          </p>
          {data.officialMapCrossCheck?.note ? (
            <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
              Notiz: {data.officialMapCrossCheck.note}
            </p>
          ) : null}
          <button
            type="button"
            onClick={resetAnswer}
            className="mt-2 text-xs font-semibold text-brand-600 hover:underline"
            data-testid="button-edit-crosscheck-answer"
          >
            Angabe ändern
          </button>
        </div>
      ) : (
        <>
          <div>
            <label className="label">Notiz (optional)</label>
            <textarea
              className="input min-h-[70px]"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="z.B. zusätzliche Verbotszone auf der offiziellen Karte …"
              data-testid="input-crosscheck-note"
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => answer(false)}
              className="flex-1 rounded-md border-2 border-[var(--color-go)] bg-[var(--color-go-bg)] px-3 py-2 text-sm font-semibold text-[var(--color-go)] hover:bg-[var(--color-go-bg)]"
              data-testid="button-crosscheck-no"
            >
              Nein
            </button>
            <button
              type="button"
              onClick={() => answer(true)}
              className="flex-1 rounded-md border-2 border-[var(--color-caution)] bg-[var(--color-caution-bg)] px-3 py-2 text-sm font-semibold text-[var(--color-caution)] hover:bg-[var(--color-caution-bg)]"
              data-testid="button-crosscheck-yes"
            >
              Ja
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function StepDateTime({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Wann ist der Flug geplant und wie lange soll er dauern?
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Datum</label>
          <input
            className="input"
            type="date"
            value={data.plannedDate}
            onChange={(e) => update({ plannedDate: e.target.value })}
          />
        </div>
        <div>
          <label className="label">Uhrzeit</label>
          <input
            className="input"
            type="time"
            value={data.plannedTime}
            onChange={(e) => update({ plannedTime: e.target.value })}
          />
        </div>
      </div>
      <div>
        <label className="label">Geplante Dauer (Minuten)</label>
        <input
          className="input"
          type="number"
          min={1}
          value={data.durationMinutes}
          onChange={(e) =>
            update({ durationMinutes: parseInt(e.target.value, 10) || 0 })
          }
        />
      </div>
      <div>
        <label className="label">Zweck des Fluges</label>
        <textarea
          className="input min-h-[70px]"
          value={data.flightPurpose}
          onChange={(e) => update({ flightPurpose: e.target.value })}
          placeholder="z.B. Foto-/Videoaufnahmen, Inspektion, Training, Kartierung …"
          data-testid="input-flight-purpose"
        />
      </div>
    </div>
  );
}

const NON_21H_GEOZONE_TYPES = [
  "Naturschutzgebiete",
  "Nationalparks",
  "FFH-Gebiete",
  "Vogelschutzgebiete",
];

function StepRestrictions({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  const { data: profile } = useQuery<{
    licenseA2?: boolean;
    allgemeinverfuegung?: unknown;
  } | null>({ queryKey: ["/api/profile"] });
  const { data: settings } = useQuery<{ wildlifeRescueMode?: boolean } | null>({
    queryKey: ["/api/settings"],
  });
  const wildlifeRescueMode = settings?.wildlifeRescueMode ?? false;
  const [wildlifeInfoOpen, setWildlifeInfoOpen] = useState(false);

  const planningCountryIso =
    data.latitude != null && data.longitude != null
      ? detectCountryFromCoordinates(data.latitude, data.longitude)
      : null;
  const inGermany = planningCountryIso === "DE";
  const canShowEnergyInfrastructure =
    planningCountryIso != null && hasOwnMap(planningCountryIso);
  const energyInfrastructureQuery = useGermanEnergyInfrastructure(
    data.latitude ?? 0,
    data.longitude ?? 0,
    5,
    planningCountryIso ?? "DE",
    inGermany && data.latitude != null && data.longitude != null,
  );
  const liveEnergyInfrastructureCount =
    (energyInfrastructureQuery.data?.counts.windTurbines ?? 0) +
    (energyInfrastructureQuery.data?.counts.powerLines ?? 0) +
    (energyInfrastructureQuery.data?.counts.powerMasts ?? 0);
  const energyInfrastructureSourcesIncomplete =
    energyInfrastructureQuery.data
      ? hasIncompleteEnergyInfrastructureSources(energyInfrastructureQuery.data)
      : false;
  const detectedEnergyInfrastructureCount =
    energyInfrastructureQuery.isSuccess && !energyInfrastructureSourcesIncomplete
      ? liveEnergyInfrastructureCount
      : Math.max(liveEnergyInfrastructureCount, data.detectedEnergyInfrastructureCount ?? 0);
  const hasDetectedEnergyInfrastructure =
    inGermany && detectedEnergyInfrastructureCount > 0;
  useEffect(() => {
    if (!inGermany) {
      if ((data.detectedEnergyInfrastructureCount ?? 0) !== 0) {
        update({ detectedEnergyInfrastructureCount: 0 });
      }
      return;
    }
    if (
      energyInfrastructureQuery.isSuccess &&
      (!energyInfrastructureSourcesIncomplete || liveEnergyInfrastructureCount > 0) &&
      data.detectedEnergyInfrastructureCount !== liveEnergyInfrastructureCount
    ) {
      update({ detectedEnergyInfrastructureCount: liveEnergyInfrastructureCount });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inGermany,
    energyInfrastructureQuery.isSuccess,
    energyInfrastructureSourcesIncomplete,
    liveEnergyInfrastructureCount,
    data.detectedEnergyInfrastructureCount,
  ]);
  const detectedBundesland =
    inGermany && data.latitude != null && data.longitude != null
      ? detectBundeslandFromCoordinates(data.latitude, data.longitude)
      : null;

  const erlaubnisse: GeoAllgemeinerlaubnis[] = useMemo(
    () => migrateAllgemeinerlaubnis(profile?.allgemeinverfuegung),
    [profile],
  );
  const hasA2 = !!profile?.licenseA2;
  const erlaubnis = detectedBundesland
    ? erlaubnisse.find((e) => e.bundesland === detectedBundesland)
    : undefined;
  const erlaubnisExpired = erlaubnis ? isErlaubnisExpired(erlaubnis) : false;
  const hasValidErlaubnis = hasA2 && !!erlaubnis && !erlaubnisExpired;
  const avInfo = detectedBundesland ? BUNDESLAND_AV_INFO[detectedBundesland] : undefined;

  const [coveredSel, setCoveredSel] = useState<Set<string>>(new Set());
  const [uncoveredSel, setUncoveredSel] = useState<Set<string>>(new Set());
  const effectiveUncoveredSel = new Set(uncoveredSel);
  if (hasDetectedEnergyInfrastructure) effectiveUncoveredSel.add("energieanlagen");
  const effectiveHasRestrictions = data.hasRestrictions || hasDetectedEnergyInfrastructure;

  function toggle(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  }

  const anyGeozoneSelected = coveredSel.size > 0 || effectiveUncoveredSel.size > 0;
  const coverage = assessAvCoverage({
    inGermany,
    detectedBundesland,
    hasA2,
    erlaubnis,
    coveredGeozoneCount: coveredSel.size,
    nonCoveredGeozoneCount: effectiveUncoveredSel.size,
  });
  const coverageComplete = coverage.covered;

  // Suggested restriction status derived from the §21h coverage assessment.
  const suggestedStatus: WizardData["restrictionStatus"] | null =
    deriveSuggestedRestrictionStatus(effectiveHasRestrictions, coverage);

  useEffect(() => {
    if (suggestedStatus && data.restrictionStatus !== suggestedStatus) {
      update({ restrictionStatus: suggestedStatus });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedStatus]);

  // Länder OHNE eigene Geozonen-Daten: die Bewertung erfolgt manuell im
  // Karten-Schritt. Hier nicht erneut fragen, sondern die dort erfasste Angabe
  // anzeigen (oder darauf hinweisen, dass sie noch fehlt).
  const ownData = hasOwnMap(data.countryIso);
  if (!ownData) {
    const recorded =
      !!data.manualRestrictionChecked ||
      data.hasRestrictions ||
      !!data.restrictionNotes;
    return (
      <div className="space-y-4">
        {wildlifeRescueMode ? (
          <WildlifeRescueHint onMoreInfo={() => setWildlifeInfoOpen(true)} />
        ) : null}
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Für dieses Land können keine Geozonen angezeigt werden. Die Bewertung
          hast du im vorigen Schritt „Karte" anhand der offiziellen Karte selbst
          vorgenommen.
        </p>
        {recorded ? (
          <div
            className={`rounded-md border p-3 ${
              data.hasRestrictions
                ? "border-[var(--color-caution)] bg-[var(--color-caution-bg)]"
                : "border-[var(--color-go)] bg-[var(--color-go-bg)]"
            }`}
            data-testid="manual-restriction-result"
          >
            <p
              className={`text-sm font-semibold ${
                data.hasRestrictions ? "text-[var(--color-caution)]" : "text-[var(--color-go)]"
              }`}
            >
              {data.hasRestrictions
                ? "Einschränkungen am Standort (laut offizieller Karte)"
                : "Keine Einschränkungen am Standort (laut offizieller Karte)"}
            </p>
            {data.restrictionNotes ? (
              <p className="mt-1 text-xs text-[var(--color-app-text-secondary)]">
                Notiz: {data.restrictionNotes}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-3 text-sm text-[var(--color-caution)]">
            Du hast im Karten-Schritt noch nicht angegeben, ob Einschränkungen
            bestehen. Gehe zurück zum Schritt „Karte", prüfe die offizielle Karte
            und beantworte die Frage.
          </div>
        )}
        <div>
          <label className="label">Notizen / Auflagen</label>
          <textarea
            className="input min-h-[90px]"
            value={data.restrictionNotes}
            onChange={(e) => update({ restrictionNotes: e.target.value })}
            placeholder="z.B. Naturschutzgebiet am Westrand, NOTAM aktiv …"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {wildlifeRescueMode ? (
        <WildlifeRescueHint onMoreInfo={() => setWildlifeInfoOpen(true)} />
      ) : null}
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Gibt es Geozonen-Einschränkungen, CTR/EDR oder NOTAMs am Standort?
      </p>
      {canShowEnergyInfrastructure && data.latitude != null && data.longitude != null ? (
        <GermanEnergyInfrastructurePanel
          countryIso={planningCountryIso ?? "DE"}
          latitude={data.latitude}
          longitude={data.longitude}
          radiusKm={5}
        />
      ) : null}
      {hasDetectedEnergyInfrastructure ? (
        <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-2 text-xs text-[var(--color-caution)]">
          Die zusätzlichen Treffer werden wie Energieanlagen in die Restriktionsprüfung einbezogen. Sie gelten nicht als durch die Landes-AV abgedeckt.
        </div>
      ) : null}
      <div className="space-y-2">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="size-4 accent-brand-500"
            checked={effectiveHasRestrictions}
            disabled={hasDetectedEnergyInfrastructure}
            onChange={(e) =>
              update({
                hasRestrictions: e.target.checked,
                restrictionStatus: e.target.checked ? "limited" : "none",
              })
            }
          />
          <span className="text-sm">
            Einschränkungen am Standort vorhanden
            {hasDetectedEnergyInfrastructure ? " (automatisch erkannt)" : ""}
          </span>
        </label>
      </div>

      {effectiveHasRestrictions && inGermany && (
        <div className="rounded-md border border-[var(--color-app-border)] bg-[var(--color-app-bg-secondary)] p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[var(--color-app-text)]">
              Geografische Allgemeinerlaubnis (§ 21h LuftVO)
            </span>
            <span className="text-xs text-[var(--color-app-text-secondary)]">
              {detectedBundesland || "Bundesland unbekannt"}
            </span>
          </div>

          {!hasA2 ? (
            <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-2 text-xs text-[var(--color-caution)]">
              Kein A2-Fernpilotenzeugnis im Profil. Eine § 21h-Allgemeinerlaubnis ist ohne A2
              nicht möglich — Geozonen gelten als nicht abgedeckt.
            </div>
          ) : !detectedBundesland ? (
            <div className="rounded-md border border-[var(--color-app-border)] bg-white p-2 text-xs text-[var(--color-app-text-secondary)]">
              Bundesland konnte aus den Koordinaten nicht bestimmt werden.
            </div>
          ) : !erlaubnis ? (
            <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-2 text-xs text-[var(--color-caution)]">
              Keine Allgemeinerlaubnis für {detectedBundesland} im Profil hinterlegt. Es gibt keine
              gegenseitige Anerkennung zwischen Bundesländern.
            </div>
          ) : erlaubnisExpired ? (
            <div className="rounded-md border border-[var(--color-nogo)] bg-[var(--color-nogo-bg)] p-2 text-xs text-[var(--color-nogo)]">
              Deine Allgemeinerlaubnis für {detectedBundesland} ist abgelaufen
              {erlaubnis.expirationDate ? ` (${erlaubnis.expirationDate})` : ""}.
            </div>
          ) : (
            <div className="rounded-md border border-[var(--color-go)] bg-[var(--color-go-bg)] p-2 text-xs text-[var(--color-go)]">
              Gültige Allgemeinerlaubnis für {detectedBundesland} vorhanden
              {erlaubnis.expirationDate ? ` (gültig bis ${erlaubnis.expirationDate})` : ""}.
            </div>
          )}

          {avInfo ? (
            <div className="space-y-0.5">
              <div className="text-[11px] text-[var(--color-app-text-secondary)]">{avInfo.behorde}</div>
              <a
                href={avInfo.antragUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-xs font-semibold text-[var(--color-brand-600)] hover:underline"
              >
                Antrag stellen (dipul.de) ↗
              </a>
              {avInfo.infoUrl ? (
                <a
                  href={avInfo.infoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs font-semibold text-[var(--color-brand-600)] hover:underline"
                >
                  Infos der Landesluftfahrtbehörde ↗
                </a>
              ) : null}
            </div>
          ) : null}

          <div>
            <div className="text-xs font-medium text-[var(--color-app-text)] mb-1">
              Durch Landes-Allgemeinerlaubnis (§ 21h) abdeckbare Geozonen-Typen
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PARAGRAPH_21H_RULES.filter((r) => r.avEligible).map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setCoveredSel((s) => toggle(s, r.id))}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    coveredSel.has(r.id)
                      ? "border-brand-500 bg-brand-50 text-brand-700"
                      : "border-[var(--color-app-border)] text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-[var(--color-app-text)] mb-1">
              Nicht durch Landes-AV abgedeckt (separate Freigabe/Genehmigung nötig)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PARAGRAPH_21H_RULES.filter((r) => !r.avEligible).map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setUncoveredSel((s) => toggle(s, r.id))}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    effectiveUncoveredSel.has(r.id)
                      ? "border-[var(--color-nogo)] bg-[var(--color-nogo-bg)] text-[var(--color-nogo)]"
                      : "border-[var(--color-app-border)] text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
              {NON_21H_GEOZONE_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setUncoveredSel((s) => toggle(s, t))}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    uncoveredSel.has(t)
                      ? "border-[var(--color-nogo)] bg-[var(--color-nogo-bg)] text-[var(--color-nogo)]"
                      : "border-[var(--color-app-border)] text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-tertiary)]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {anyGeozoneSelected ? (
            coverageComplete ? (
              <div className="rounded-md border border-[var(--color-go)] bg-[var(--color-go-bg)] p-2 text-xs text-[var(--color-go)]">
                Alle ausgewählten Geozonen sind durch deine gültige Allgemeinerlaubnis für{" "}
                {detectedBundesland} abgedeckt (Status: „Genehmigung liegt vor").
              </div>
            ) : (
              <div className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-2 text-xs text-[var(--color-caution)]">
                {effectiveUncoveredSel.size > 0
                  ? "Mindestens eine Geozone ist nicht durch die Landes-Allgemeinerlaubnis abgedeckt (z. B. Flugplätze/HEMS, Flughäfen/CTR, Industrie-/Energieanlagen, Behörden oder Natur-/Vogelschutz, Nationalpark, FFH) und erfordert eine separate Freigabe/Genehmigung (z. B. DFS/ATC, Betreiber, zuständige Behörde)."
                  : "Ohne gültige Allgemeinerlaubnis für dieses Bundesland sind die Geozonen nicht abgedeckt — Flug nur mit Auflagen/Genehmigung."}
              </div>
            )
          ) : null}
        </div>
      )}

      {effectiveHasRestrictions && (
        <>
          <div>
            <label className="label">Status</label>
            <div className="flex flex-col sm:flex-row gap-2">
              {[
                { v: "approved", l: "Genehmigung liegt vor" },
                { v: "limited", l: "Mit Auflagen fliegbar" },
                { v: "none", l: "Keine Genehmigung" },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className={`flex-1 cursor-pointer rounded-md border-2 px-3 py-2 text-sm ${
                    data.restrictionStatus === opt.v
                      ? "border-brand-500 bg-brand-50"
                      : "border-[var(--color-app-border)]"
                  }`}
                >
                  <input
                    type="radio"
                    name="restrictionStatus"
                    className="mr-2 accent-brand-500"
                    checked={data.restrictionStatus === opt.v}
                    onChange={() =>
                      update({ restrictionStatus: opt.v as WizardData["restrictionStatus"] })
                    }
                  />
                  {opt.l}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Notizen / Auflagen</label>
            <textarea
              className="input min-h-[90px]"
              value={data.restrictionNotes}
              onChange={(e) => update({ restrictionNotes: e.target.value })}
              placeholder="z.B. CTR EDDF: Außenbereich, max. Höhe je nach Flughafenentfernung (0–100 m AGL) …"
            />
          </div>
        </>
      )}
      <WildlifeRescueInfoModal open={wildlifeInfoOpen} onClose={() => setWildlifeInfoOpen(false)} />
    </div>
  );
}

function WildlifeRescueHint({ onMoreInfo }: { onMoreInfo: () => void }) {
  return (
    <button
      type="button"
      onClick={onMoreInfo}
      className="w-full text-left rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-3"
      data-testid="hint-wildlife-rescue"
    >
      <div className="flex items-center gap-2 mb-1">
        <PawPrint size={16} className="text-[var(--color-caution)] shrink-0" />
        <span className="text-sm font-semibold text-[var(--color-caution)]">
          {WILDLIFE_RESCUE_MODE_TITLE}
        </span>
      </div>
      <p className="text-xs text-[var(--color-app-text)]">{WILDLIFE_RESCUE_PLANNING_HINT}</p>
      <span className="mt-2 inline-block text-xs font-semibold text-brand-600">
        Mehr Infos zu den Allgemeinverfügungen
      </span>
    </button>
  );
}

function StepNotams({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  const [rows, setRows] = useState<NotamRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    if (data.latitude == null || data.longitude == null) {
      setErr("Bitte zuerst Standort eingeben.");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = await fetchNotams(data.latitude, data.longitude);
      setRows(res.rows || []);
      update({
        notamCount: res.rows?.length || 0,
        notamCheckedAt: new Date().toISOString(),
      });
    } catch (e) {
      setErr((e as Error).message || "NOTAMs konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (rows == null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.latitude, data.longitude]);

  function fmtTs(t?: number) {
    if (!t) return "—";
    return new Date(t * 1000).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          NOTAMs im Umkreis deines Startpunkts (autorouter).
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-secondary text-xs"
          data-testid="button-notam-refresh"
        >
          {loading ? "Lädt …" : "Neu laden"}
        </button>
      </div>

      {err && (
        <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded-md px-3 py-2">
          {err}
        </div>
      )}

      {!loading && rows && rows.length === 0 && (
        <div className="text-sm text-[var(--color-go)] bg-[var(--color-go-bg)] border border-[var(--color-go)] rounded-md px-3 py-2">
          Keine aktiven NOTAMs in Reichweite gefunden.
        </div>
      )}

      {rows && rows.length > 0 && (
        <>
          <div className="text-sm text-[var(--color-caution)] bg-[var(--color-caution-bg)] border border-[var(--color-caution)] rounded-md px-3 py-2">
            {rows.length} NOTAM(s) in der Nähe — bitte prüfen, ob dein Flug betroffen ist.
          </div>
          <ul className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {rows.slice(0, 25).map((n, i) => (
              <li
                key={i}
                className="rounded-md border border-[var(--color-app-border)] px-3 py-2 text-xs"
                data-testid={`notam-row-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-[var(--color-app-text)]">
                    {n.ItemA || `NOTAM ${i + 1}`}
                  </span>
                  <span className="text-[var(--color-app-text-secondary)]">
                    {fmtTs(n.StartValidity)} – {fmtTs(n.EndValidity)}
                  </span>
                </div>
                {n.ItemE && (
                  <div className="mt-1 text-[var(--color-app-text)] whitespace-pre-wrap">
                    {n.ItemE}
                  </div>
                )}
              </li>
            ))}
            {rows.length > 25 && (
              <li className="text-xs text-[var(--color-app-text-secondary)] px-1">
                … {rows.length - 25} weitere
              </li>
            )}
          </ul>
        </>
      )}

      {data.notamCheckedAt && (
        <div className="text-xs text-[var(--color-app-text-secondary)]">
          Zuletzt geprüft: {new Date(data.notamCheckedAt).toLocaleString("de-DE")}
        </div>
      )}
    </div>
  );
}

function StepWeather({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  const [forecast, setForecast] = useState<ForecastResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const targetIso = useMemo(() => {
    return new Date(`${data.plannedDate}T${data.plannedTime}:00`).toISOString();
  }, [data.plannedDate, data.plannedTime]);

  async function load() {
    if (data.latitude == null || data.longitude == null) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await fetchForecast(data.latitude, data.longitude, targetIso);
      setForecast(res);
      update({ forecastWeather: res.current });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.latitude, data.longitude, targetIso]);

  const w = forecast?.current;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Vorhersage für {data.plannedDate} {data.plannedTime} (Open-Meteo)
        </p>
        <button onClick={load} disabled={loading} className="btn-secondary text-xs">
          {loading ? "Lädt …" : "Aktualisieren"}
        </button>
      </div>

      {err && (
        <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded-md px-3 py-2">
          {err}
        </div>
      )}

      {w && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Metric label="Temperatur" value={`${w.temperature.toFixed(1)} °C`} />
            <Metric
              label="Wind"
              value={`${w.windSpeed.toFixed(1)} m/s`}
              hint={describeWind(w.windSpeed)}
            />
            <Metric
              label="Böen"
              value={w.windGust ? `${w.windGust.toFixed(1)} m/s` : "—"}
            />
            <Metric label="Niederschlag" value={`${w.rain.toFixed(2)} mm/h`} />
            <Metric label="Bewölkung" value={`${w.cloudCover.toFixed(0)} %`} />
            <Metric label="Luftfeuchte" value={`${w.humidity.toFixed(0)} %`} />
          </div>

          {forecast && (
            <div>
              <div className="text-xs uppercase text-[var(--color-app-text-secondary)] font-semibold mb-2">
                Verlauf nächste Stunden
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[var(--color-app-text-secondary)]">
                    <tr className="text-left">
                      <th className="py-1 pr-3">Zeit</th>
                      <th className="py-1 pr-3">°C</th>
                      <th className="py-1 pr-3">Wind</th>
                      <th className="py-1 pr-3">Böen</th>
                      <th className="py-1 pr-3">Regen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecast.hours.slice(0, 8).map((h) => (
                      <tr key={h.time} className="border-t border-[var(--color-app-border)]">
                        <td className="py-1 pr-3">
                          {new Date(h.time).toLocaleTimeString("de-DE", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="py-1 pr-3">{h.temperature.toFixed(1)}</td>
                        <td className="py-1 pr-3">{h.windSpeed.toFixed(1)}</td>
                        <td className="py-1 pr-3">{h.windGust.toFixed(1)}</td>
                        <td className="py-1 pr-3">{h.rain.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!w && !loading && !err && (
        <p className="text-sm text-[var(--color-app-text-secondary)]">Keine Wetterdaten geladen.</p>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="bg-[var(--color-app-bg-secondary)] rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase text-[var(--color-app-text-secondary)] font-medium">{label}</div>
      <div className="text-base font-semibold text-[var(--color-app-text)]">{value}</div>
      {hint && <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">{hint}</div>}
    </div>
  );
}

function StepFpv({
  data,
  update,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Fliegst du diesen Flug im FPV-Modus (mit Brille)? Im FPV-Modus ist ein
        Spotter (Beobachter mit VLOS) zwingend erforderlich.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {[
          { v: false, l: "Standard / VLOS", d: "Ich sehe die Drohne mit den eigenen Augen." },
          { v: true, l: "FPV-Flug", d: "Mit Brille — Spotter erforderlich." },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type="button"
            onClick={() => update({ isFpvFlight: opt.v })}
            className={`text-left rounded-lg border-2 px-4 py-3 ${
              data.isFpvFlight === opt.v
                ? "border-brand-500 bg-brand-50"
                : "border-[var(--color-app-border)]"
            }`}
          >
            <div className="font-semibold text-[var(--color-app-text)]">{opt.l}</div>
            <div className="text-xs text-[var(--color-app-text-secondary)] mt-1">{opt.d}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepChecklist({
  data,
  update,
  drone,
}: {
  data: WizardData;
  update: (p: Partial<WizardData>) => void;
  drone?: Drone;
}) {
  const sections = useMemo(() => {
    const base = [...PLANNING_CHECKLIST];
    if (data.isFpvFlight) {
      base.push({ title: "FPV-Vorbereitung", items: FPV_PLANNING_ITEMS });
    }
    return base;
  }, [data.isFpvFlight]);

  function toggle(id: string) {
    const set = new Set(data.checklistCompleted);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    update({ checklistCompleted: Array.from(set) });
  }

  const allRequired = sections.flatMap((s) => s.items.filter((i) => i.required));
  const missing = allRequired.filter((i) => !data.checklistCompleted.includes(i.id));

  return (
    <div className="space-y-5">
      <p className="text-sm text-[var(--color-app-text-secondary)]">
        Bitte alle Pflichtpunkte für die Drohne {drone?.name || ""} bestätigen.
      </p>
      {sections.map((section) => (
        <div key={section.title}>
          <div className="text-xs uppercase text-[var(--color-app-text-secondary)] font-semibold mb-2">
            {section.title}
          </div>
          <div className="space-y-2">
            {section.items.map((item) => {
              const done = data.checklistCompleted.includes(item.id);
              return (
                <label
                  key={item.id}
                  className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer ${
                    done
                      ? "border-[var(--color-go)] bg-[var(--color-go-bg)]"
                      : "border-[var(--color-app-border)] hover:border-[var(--color-app-border)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-[var(--color-go)]"
                    checked={done}
                    onChange={() => toggle(item.id)}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-[var(--color-app-text)]">
                      {item.label}
                      {item.required && (
                        <span className="ml-2 text-[10px] uppercase text-[var(--color-nogo)] font-semibold">
                          Pflicht
                        </span>
                      )}
                    </div>
                    {item.tip && (
                      <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">{item.tip}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {missing.length > 0 ? (
        <div className="text-xs text-[var(--color-caution)] bg-[var(--color-caution-bg)] border border-[var(--color-caution)] rounded-md px-3 py-2">
          Noch offen: {missing.map((i) => i.label).join(", ")}
        </div>
      ) : (
        <div className="text-xs text-[var(--color-go)] bg-[var(--color-go-bg)] border border-[var(--color-go)] rounded-md px-3 py-2">
          Alle Pflichtpunkte abgehakt.
        </div>
      )}
    </div>
  );
}

function StepResult({
  data,
  drone,
  limits,
  sessionId,
  persist,
  navigate,
  qc,
}: {
  data: WizardData;
  drone?: Drone;
  limits: Omit<typeof DEFAULT_LIMITS, "maxTemp"> & { maxTemp?: number };
  sessionId: string | null;
  persist: (extra?: Partial<FlightSession>) => Promise<FlightSession | undefined>;
  navigate: ReturnType<typeof useNavigate>;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const checklistMissing = useMemo(() => {
    const required = [
      ...PLANNING_CHECKLIST.flatMap((s) => s.items.filter((i) => i.required)),
      ...(data.isFpvFlight ? FPV_PLANNING_ITEMS.filter((i) => i.required) : []),
    ];
    return required.filter((i) => !data.checklistCompleted.includes(i.id));
  }, [data.checklistCompleted, data.isFpvFlight]);
  const hasGermanEnergyInfrastructure =
    data.countryIso === "DE" &&
    (data.detectedEnergyInfrastructureCount ?? 0) > 0;
  const hasRestrictionsForReview =
    data.hasRestrictions || hasGermanEnergyInfrastructure;

  const assessment = useMemo(() => {
    if (!data.forecastWeather) {
      return {
        status: "caution" as const,
        warnings: [
          { message: "Keine Wetterdaten — Bewertung unvollständig", severity: "caution" as const },
        ],
        reasons: [{ type: "caution" as const, message: "Wetterdaten fehlen" }],
      };
    }
    return assessPlanning(data.forecastWeather, limits, {
      hasRestrictions: hasRestrictionsForReview,
      restrictionStatus: data.restrictionStatus,
      maxAltitude: data.maxAltitude,
      upcomingHours: [],
    });
  }, [data, hasRestrictionsForReview, limits]);

  // If required checklist items missing → downgrade
  let finalStatus = assessment.status;
  if (checklistMissing.length > 0 && finalStatus === "go") finalStatus = "caution";

  // Gegenprüfung der offiziellen Karte: startverhindernde Geozonen heben den
  // Status auf mindestens "caution" an (ersetzt NICHT die Auto-Erkennung).
  const crossCheck = data.officialMapCrossCheck;
  const crossCheckBlocking = crossCheck?.blocking === true;
  if (crossCheckBlocking && finalStatus === "go") finalStatus = "caution";
  const reasonsWithCrossCheck = crossCheckBlocking
    ? [
        ...assessment.reasons,
        {
          type: "caution" as const,
          message: crossCheck?.note
            ? `Offizielle Karte: startverhindernde Geozonen gemeldet. Notiz: ${crossCheck.note}`
            : "Offizielle Karte: startverhindernde Geozonen gemeldet.",
        },
      ]
    : assessment.reasons;

  const [savedId, setSavedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setErr(null);
    try {
      const saved = await persist({
        status: finalStatus,
        reasons: reasonsWithCrossCheck,
      });
      if (saved) setSavedId(saved.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLaunch() {
    setLaunching(true);
    setErr(null);
    try {
      const saved = await persist({
        currentPhase: "preflight",
        status: finalStatus,
        reasons: reasonsWithCrossCheck,
      });
      if (saved) {
        setSavedId(saved.id);
        qc.invalidateQueries({ queryKey: ["/api/sessions"] });
        navigate(`/flug/${saved.id}`);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLaunching(false);
    }
  }

  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg p-5 text-white ${STATUS_COLORS[finalStatus]}`}
      >
        <div className="text-xs uppercase tracking-wide opacity-80">
          Empfehlung
        </div>
        <div className="text-3xl font-bold mt-1">
          {STATUS_LABELS[finalStatus]}
        </div>
        <div className="text-sm mt-2 opacity-90">
          {finalStatus === "go" && "Alle geprüften Bedingungen liegen innerhalb der Limits."}
          {finalStatus === "caution" &&
            "Flug grundsätzlich möglich, aber bitte Hinweise beachten."}
          {finalStatus === "nogo" &&
            "Flug wird nicht empfohlen — kritische Einschränkungen erkannt."}
        </div>
      </div>

      <Section title="Eckdaten">
        <ul className="text-sm text-[var(--color-app-text)] space-y-1">
          <li>
            <span className="text-[var(--color-app-text-secondary)]">Drohne:</span> {drone?.name || "—"} (
            {drone?.classMark || "—"} · {drone?.openCategory || "—"})
          </li>
          <li>
            <span className="text-[var(--color-app-text-secondary)]">Standort:</span>{" "}
            {data.locationName || "—"} ({data.latitude?.toFixed(4)},{" "}
            {data.longitude?.toFixed(4)}) · {data.countryIso}
          </li>
          <li>
            <span className="text-[var(--color-app-text-secondary)]">Zeit:</span> {data.plannedDate}{" "}
            {data.plannedTime} · {data.durationMinutes} min
          </li>
          <li>
            <span className="text-[var(--color-app-text-secondary)]">Max. Höhe:</span> {data.maxAltitude} m
            AGL
          </li>
          <li>
            <span className="text-[var(--color-app-text-secondary)]">Modus:</span>{" "}
            {data.isFpvFlight ? "FPV (mit Spotter)" : "VLOS / Standard"}
          </li>
          {data.flightPurpose && (
            <li>
              <span className="text-[var(--color-app-text-secondary)]">Zweck:</span>{" "}
              <span className="whitespace-pre-wrap">{data.flightPurpose}</span>
            </li>
          )}
          {data.notamCheckedAt != null && (
            <li>
              <span className="text-[var(--color-app-text-secondary)]">NOTAMs:</span>{" "}
              {data.notamCount === 0
                ? "keine in der Nähe"
                : `${data.notamCount ?? "?"} in der Nähe — bitte vor Start prüfen`}
            </li>
          )}
        </ul>
      </Section>

      {data.forecastWeather && (
        <Section title="Wetter zum geplanten Zeitpunkt">
          <ul className="text-sm text-[var(--color-app-text)] space-y-1">
            <li>
              {data.forecastWeather.temperature.toFixed(1)} °C ·{" "}
              {data.forecastWeather.windSpeed.toFixed(1)} m/s Wind
              {data.forecastWeather.windGust
                ? ` · ${data.forecastWeather.windGust.toFixed(1)} m/s Böen`
                : ""}
            </li>
            <li>
              {data.forecastWeather.rain.toFixed(2)} mm/h Regen ·{" "}
              {data.forecastWeather.cloudCover.toFixed(0)} % Bewölkung ·{" "}
              {data.forecastWeather.humidity.toFixed(0)} % rH
            </li>
          </ul>
        </Section>
      )}

      {hasRestrictionsForReview && (
        <Section title="Restriktionen">
          <p className="text-sm">
            Status:{" "}
            <span className="font-medium">
              {data.restrictionStatus === "approved"
                ? "Genehmigung liegt vor"
                : data.restrictionStatus === "limited"
                ? "Mit Auflagen"
                : "Keine Genehmigung"}
            </span>
          </p>
          {data.restrictionNotes && (
            <p className="text-sm text-[var(--color-app-text-secondary)] mt-1 whitespace-pre-wrap">
              {data.restrictionNotes}
            </p>
          )}
          {hasGermanEnergyInfrastructure ? (
            <p className="text-sm text-[var(--color-app-text-secondary)] mt-1">
              Zusätzlich berücksichtigt: {data.detectedEnergyInfrastructureCount} MaStR-/OSM-Treffer, die DIPUL nicht bestätigt.
            </p>
          ) : null}
        </Section>
      )}

      {crossCheckBlocking && (
        <div
          className="rounded-md border border-[var(--color-caution)] bg-[var(--color-caution-bg)] p-4"
          data-testid="card-crosscheck-warning"
        >
          <p className="text-sm font-semibold text-[var(--color-caution)]">
            Gegenprüfung: offizielle Karte
          </p>
          <p className="mt-1 text-sm text-[var(--color-caution)]">
            Du hast bei der Gegenprüfung der offiziellen Staatskarte angegeben, dass
            dort Geozonen einen Start verhindern würden. Bitte vor dem Flug prüfen.
          </p>
          {crossCheck?.note && (
            <p className="mt-1 text-xs text-[var(--color-caution)]">Notiz: {crossCheck.note}</p>
          )}
        </div>
      )}

      <Section title="Bewertungs-Begründung">
        <ul className="text-sm space-y-1">
          {reasonsWithCrossCheck.map((r, i) => (
            <li key={i} className="flex gap-2">
              <span
                className={`inline-block size-2 rounded-full mt-1.5 ${
                  r.type === "go"
                    ? "bg-[var(--color-go)]"
                    : r.type === "caution"
                    ? "bg-[var(--color-caution)]"
                    : "bg-[var(--color-nogo)]"
                }`}
              />
              <span className="text-[var(--color-app-text)]">{r.message}</span>
            </li>
          ))}
        </ul>
        {assessment.warnings.length > 0 && (
          <div className="mt-3 space-y-1">
            {assessment.warnings.map((w, i) => (
              <div
                key={i}
                className={`text-xs rounded-md px-3 py-2 border ${
                  w.severity === "warning"
                    ? "bg-[var(--color-nogo-bg)] border-[var(--color-nogo)] text-[var(--color-nogo)]"
                    : "bg-[var(--color-caution-bg)] border-[var(--color-caution)] text-[var(--color-caution)]"
                }`}
              >
                {w.message}
              </div>
            ))}
          </div>
        )}
      </Section>

      {checklistMissing.length > 0 && (
        <Section title="Offene Pflichtpunkte">
          <ul className="text-sm text-[var(--color-caution)] list-disc pl-5 space-y-0.5">
            {checklistMissing.map((i) => (
              <li key={i.id}>{i.label}</li>
            ))}
          </ul>
        </Section>
      )}

      {err && (
        <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded-md px-3 py-2">
          {err}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate("/flugplanung")}
        >
          Zurück zur Übersicht
        </button>
        <button
          type="button"
          className="btn-secondary flex-1"
          disabled={saving || launching}
          onClick={handleSave}
          data-testid="button-save-planning"
        >
          {saving
            ? "Speichert …"
            : savedId
            ? "Gespeichert ✓"
            : sessionId
            ? "Planung aktualisieren"
            : "Planung speichern"}
        </button>
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={saving || launching || finalStatus === "nogo"}
          onClick={handleLaunch}
          data-testid="button-launch-preflight"
          title={
            finalStatus === "nogo"
              ? "Bewertung ist NO-GO — Flug nicht empfohlen."
              : undefined
          }
        >
          {launching ? "Startet …" : "Pre-Flight starten →"}
        </button>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-[var(--color-app-border)] p-4">
      <div className="text-xs uppercase text-[var(--color-app-text-secondary)] font-semibold mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
